/**
 * Map functionality - Leaflet map, markers, tracking beam
 */

import {
    MAP_TILES,
    MAP_CENTER,
    MAP_ZOOM,
    MAP_MAX_ZOOM,
    MAP_PADDING,
    SERVICES,
    BURST_ACCENT_COLOR,
} from './config.js';
import {
    userLocation,
    markers,
    alerts,
    activeFilters,
    allDisplayedAlerts,
    activeBursts,
} from './state.js';
import { formatAddressHtml, getDistanceKm, getBurstUrl, escapeHtml } from './utils.js';

// Map and layer references
export let map = null;
let tileLayer = null;
let userLocationMarker = null;
let trackingBeam = null;
let routePolyline = null;  // For showing actual driving route
// The service each line was drawn for, so a theme change can recolour it.
let trackingBeamService = null;
let routePolylineService = null;
const burstCircles = new Map(); // burst id -> { circle, badge, isLive }


/**
 * Initialize the Leaflet map
 */
export function initMap() {
    document.getElementById('map-placeholder')?.remove();
    map = L.map('map', {
        zoomControl: false,
        attributionControl: true,
    }).setView(MAP_CENTER, MAP_ZOOM);

    tileLayer = L.tileLayer(MAP_TILES.url, {
        attribution: MAP_TILES.attribution,
        maxZoom: MAP_MAX_ZOOM,
        maxNativeZoom: MAP_TILES.maxNativeZoom,
        alt: 'Kaart van Nederland met hulpdienst meldingen',
    }).addTo(map);

    // Overlap counts are pixel-based, so they must recompute whenever the
    // projection changes (zoom, or a pan that shifts cell boundaries). The
    // burst-label declutter is pixel-based for the same reason.
    map.on('zoomend moveend', updateOverlapBadges);
    map.on('zoomend moveend', declutterBurstLabels);

    return map;
}


/**
 * Set user location marker on map (with eyes emoji)
 * @param {number} lat
 * @param {number} lon
 */
export function setUserMarker(lat, lon) {
    if (userLocationMarker) {
        map.removeLayer(userLocationMarker);
    }

    userLocationMarker = L.marker([lat, lon], {
        icon: L.divIcon({
            className: 'user-location-marker',
            html: `<div class="user-marker-icon">
                <span class="radar-ring r1"></span>
                <span class="radar-ring r2"></span>
                <span class="radar-ring r3"></span>
                <span class="radar-ring r4"></span>
                <span class="radar-ring r5"></span>
            </div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
        }),
    }).addTo(map);

    return userLocationMarker;
}


/**
 * Remove user location marker
 */
export function removeUserMarker() {
    if (userLocationMarker) {
        map.removeLayer(userLocationMarker);
        userLocationMarker = null;
    }
}


/**
 * Create an alert marker
 * @param {Object} alert
 * @param {Function} onClickCallback
 * @returns {L.Marker}
 */
export function createMarker(alert, onClickCallback) {
    // Check if outside the displayed set (top N closest)
    const displayedIds = new Set(allDisplayedAlerts.map(a => a.id));
    const outsideRadius = displayedIds.size > 0 && !displayedIds.has(alert.id);

    const marker = L.marker([alert.latitude, alert.longitude], {
        icon: L.divIcon({
            className: 'custom-marker',
            // Flat coloured dot — no emoji glyph. The service colour is the
            // signal (matches the panel cards and the server-page maps).
            html: `<div class="marker-icon ${alert.service} ${outsideRadius ? 'outside-radius' : ''}"></div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        }),
    });

    // Cause-led popup: line 1 = the short "what?" word, line 2 = location_short
    // via formatAddressHtml (city is the inline link). Both lines escape
    // user-derived text — formatAddressHtml escapes street and city
    // internally; primary is escaped here.
    const display = alert.display;
    const primary = display?.primary || alert.primary_title || '';
    const locationShort = display?.location_short || alert.address || '';
    const popupPrimary = primary
        ? `<div class="popup-primary">${escapeHtml(primary)}</div>`
        : '';
    const popupAddress = locationShort
        ? `<div class="popup-address">${formatAddressHtml(locationShort)}</div>`
        : (primary ? '' : '<div class="popup-address">Locatie onbekend</div>');
    marker.bindPopup(
        `<div class="popup-content">${popupPrimary}${popupAddress}</div>`,
        { offset: [0, -14] }
    );

    // Click handler
    if (onClickCallback) {
        marker.on('click', () => onClickCallback(alert.id));
    }

    return marker;
}


/**
 * Add marker to map (if filter active)
 * @param {string} id
 * @param {L.Marker} marker
 * @param {string} service
 */
export function addMarkerToMap(id, marker, service) {
    markers.set(id, marker);
    if (activeFilters.has(service)) {
        marker.addTo(map);
    }
}


/**
 * Remove marker from map
 * @param {string} id
 */
export function removeMarkerFromMap(id) {
    const marker = markers.get(id);
    if (marker) {
        map.removeLayer(marker);
        markers.delete(id);
    }
}


/**
 * Highlight active marker
 * @param {string} activeId
 */
export function highlightMarker(activeId) {
    markers.forEach((marker, id) => {
        const el = marker.getElement();
        if (el) {
            const icon = el.querySelector('.marker-icon');
            if (icon) {
                icon.classList.toggle('active', id === activeId);
            }
        }
    });
}


/**
 * Open popup for marker
 * @param {string} id
 */
export function openMarkerPopup(id) {
    // Close all popups first
    markers.forEach(m => m.closePopup());

    const marker = markers.get(id);
    if (marker) {
        marker.openPopup();
    }
}


/**
 * Update tracking beam from user to alert
 * @param {Object} alert
 */
export function updateTrackingBeam(alert) {
    // Remove existing beam
    if (trackingBeam) {
        map.removeLayer(trackingBeam);
        trackingBeam = null;
    }

    // Only draw if we have both user location and alert location
    if (!userLocation || !alert?.latitude || !alert?.longitude) return;

    const color = SERVICES[alert.service]?.color || '#666';
    trackingBeamService = alert.service;

    trackingBeam = L.polyline(
        [[userLocation.lat, userLocation.lon], [alert.latitude, alert.longitude]],
        {
            color: color,
            weight: 3,
            opacity: 0.6,
            dashArray: '10, 10',
            className: 'tracking-beam'
        }
    ).addTo(map);
}


/**
 * Fit map to show both user location and alert
 * Ensures both markers are visible with generous padding
 * @param {Object} alert
 */
export function fitBoundsToAlert(alert) {
    if (!userLocation || !alert?.latitude || !alert?.longitude) return;

    const userLatLng = L.latLng(userLocation.lat, userLocation.lon);
    const alertLatLng = L.latLng(alert.latitude, alert.longitude);

    // Create bounds from both points
    let bounds = L.latLngBounds(userLatLng, alertLatLng);

    // Pad the bounds by 10% to ensure markers aren't at the edge
    bounds = bounds.pad(0.1);

    // Calculate distance to determine appropriate max zoom
    const distanceKm = getDistanceKm(
        userLocation.lat, userLocation.lon,
        alert.latitude, alert.longitude
    );

    // Adaptive maxZoom based on distance
    // Close (<5km): zoom 14, Medium (5-20km): zoom 12, Far (>20km): zoom 10
    let maxZoom = 14;
    if (distanceKm > 20) {
        maxZoom = 10;
    } else if (distanceKm > 5) {
        maxZoom = 12;
    }

    // Mobile: account for single card at bottom (~130px) + top bar (60px)
    const isMobile = window.innerWidth <= 768;
    const padTopLeft = isMobile ? [20, 60] : MAP_PADDING.paddingTopLeft;
    const padBottomRight = isMobile ? [20, 150] : MAP_PADDING.paddingBottomRight;

    map.fitBounds(bounds, {
        paddingTopLeft: padTopLeft,
        paddingBottomRight: padBottomRight,
        maxZoom: maxZoom,
        animate: true,
        duration: 0.3
    });
}


/**
 * Set map view to coordinates
 * @param {number} lat
 * @param {number} lon
 * @param {number} zoom
 */
export function setView(lat, lon, zoom = 14) {
    map.setView([lat, lon], zoom, { animate: true });
}


/**
 * Update marker opacities - dim markers not in the displayed set (top N closest)
 */
export function updateMarkerOpacities() {
    const displayedIds = new Set(allDisplayedAlerts.map(a => a.id));

    markers.forEach((marker, id) => {
        const el = marker.getElement();
        if (el) {
            const icon = el.querySelector('.marker-icon');
            if (icon) {
                icon.classList.toggle('outside-radius', displayedIds.size > 0 && !displayedIds.has(id));
            }
        }
    });
}


/**
 * Show a route polyline on the map (actual driving route)
 * @param {Array<[number, number]>} coordinates - Array of [lat, lon] pairs
 * @param {Object} alert - The target alert (for color)
 */
export function showRoutePolyline(coordinates, alert) {
    // Remove existing route
    clearRoutePolyline();

    if (!coordinates || coordinates.length === 0) return;

    const color = SERVICES[alert?.service]?.color || '#3388ff';
    routePolylineService = alert?.service;

    routePolyline = L.polyline(coordinates, {
        color: color,
        weight: 4,
        opacity: 0.8,
        className: 'route-polyline',
    }).addTo(map);

    // Hide the dashed tracking beam when showing route
    if (trackingBeam) {
        map.removeLayer(trackingBeam);
        trackingBeam = null;
    }
}


/**
 * Clear the route polyline from the map
 */
export function clearRoutePolyline() {
    if (routePolyline) {
        map.removeLayer(routePolyline);
        routePolyline = null;
    }
}


// Radius of the burst circle overlay, and the same distance used to decide
// which alert markers fall "inside" a burst (so the ring matches the circle).
const BURST_CIRCLE_RADIUS_M = 1500;

// Live bursts pulse; recently-ended "story" bursts get a calmer, static
// treatment so a settled summary reads as something to open, not a fresh
// spike. Both use the shared --burst-color token.
const _LIVE_STYLE = {
    className: 'burst-circle-overlay',
    fillOpacity: 0.14,
    weight: 3.5,
    opacity: 0.9,
};
const _STORY_STYLE = {
    className: 'burst-circle-story',
    fillOpacity: 0.10,
    weight: 3,
    opacity: 0.78,
};


/**
 * Build the burst circle + optional "nieuws" badge marker for one burst.
 * className can't be swapped on an existing Leaflet path, so callers recreate
 * the pair whenever a burst flips live↔story.
 */
// Popup card HTML for a burst. Split out so the update path can refresh it —
// a live burst's AI title / count arrive after detection, and the popup must
// pick them up, not stay frozen at creation. Signature below gates the refresh.
function _burstPopupHtml(burst) {
    const label = escapeHtml(burst.scope_label || burst.scope || '');
    const url = getBurstUrl(burst);
    const title = burst.title ? escapeHtml(burst.title) : '';
    const count = burst.current_count || burst.peak_count || 0;
    const minutes = burst.window_minutes || 0;
    const isLive = burst.is_active !== false;
    const newsCount = burst.news_article_count || 0;

    // Scope eyebrow only when there's an AI title to headline (else the title
    // row already carries the scope). Meta = count (+ live window) + news chip.
    const eyebrow = title ? `<div class="burst-pop-scope">${label}</div>` : '';
    const countText = `${count} melding${count === 1 ? '' : 'en'}`
        + (isLive && minutes ? ` in ${minutes} min` : '');
    const newsChip = newsCount > 0
        ? `<span class="burst-pop-news">📰 ${newsCount} nieuwsartikel${newsCount === 1 ? '' : 'en'}</span>`
        : '';
    return `<div class="popup-content burst-pop">
            ${eyebrow}
            <div class="burst-pop-title">${title || label}</div>
            <div class="burst-pop-meta"><span>${countText}</span>${newsChip}</div>
            <a href="${url}" class="burst-pop-cta">Bekijk details</a>
        </div>`;
}

// Fields the popup card renders — recompute the popup only when one changes.
function _burstPopupSig(burst) {
    return [burst.title, burst.current_count, burst.peak_count,
        burst.news_article_count, burst.window_minutes, burst.is_active].join('|');
}

// Centre-label text for an ended "story" burst — the AI title, falling back to
// the scope name before the title lands. Empty for live bursts (they show pins).
function _burstLabelText(burst) {
    if (burst.is_active !== false) return '';
    return burst.title || burst.scope_label || burst.scope || '';
}

// Declutter/stacking priority: news-linked pieken are the editorially loud ones
// and win, then bigger bursts. Higher = placed first (sits nearest its dot) and
// floats on top.
function _burstLabelPriority(burst) {
    const news = burst.news_article_count || 0;
    const count = burst.current_count || burst.peak_count || 0;
    return (news > 0 ? 1000 : 0) + count;
}

// Recreate key for the centre label: the divIcon html can't be toggled in place,
// so we rebuild the marker when the visible text OR the baked-in news count
// changes (the badge number is rendered into the html at creation time).
function _burstLabelKey(burst) {
    const news = burst.news_article_count || 0;
    return `${_burstLabelText(burst)}::${news}`;
}

// Centre-label divIcon html. A tag-language pill — a class of object no round
// alert pin resembles: a quiet white tag with a leading piek-dot, or (when the
// burst has drawn news) a solid-accent "hero" pill with a 📰 count. The dot +
// connector anchor it to the centroid; ``--dy`` is set by the declutter pass to
// lift colliding pills clear of one another.
function _burstLabelHtml(burst, labelText) {
    const news = burst.news_article_count || 0;
    const isNews = news > 0;
    const pillClass = isNews ? 'piek-label__pill piek-label__pill--news' : 'piek-label__pill';
    const newsBadge = isNews ? `<span class="piek-label__badge">📰 ${news}</span>` : '';
    return `<div class="piek-label" style="--dy:0px">
        <span class="piek-label__connector"></span>
        <span class="piek-label__dot"></span>
        <span class="${pillClass}">${newsBadge}<span class="piek-label__text">${escapeHtml(labelText)}</span></span>
    </div>`;
}


function _createBurstLayers(burst) {
    const color = BURST_ACCENT_COLOR;
    const center = [burst.centroid_lat, burst.centroid_lon];
    const isLive = burst.is_active !== false;
    const style = isLive ? _LIVE_STYLE : _STORY_STYLE;

    const circle = L.circle(center, {
        radius: BURST_CIRCLE_RADIUS_M,
        color,
        fillColor: color,
        interactive: true,
        ...style,
    }).addTo(map);
    circle.bindPopup(_burstPopupHtml(burst), { offset: [0, 0], minWidth: 210 });

    // Ended "story" bursts persist for 24h after their alert pins age off the
    // map, leaving an empty ring. Give them a tappable centre label (the AI
    // title) as their on-map identity + tap target — it also opens the popup.
    // Live bursts keep their pins, so a label would just cover them.
    const labelText = _burstLabelText(burst);
    let label = null;
    if (labelText) {
        const isNews = (burst.news_article_count || 0) > 0;
        label = L.marker(center, {
            icon: L.divIcon({
                className: 'burst-centre-label-icon',
                html: _burstLabelHtml(burst, labelText),
                iconSize: null,
            }),
            interactive: true,
            keyboard: false,
            // Float above the alert pins at the centroid; news pills outrank
            // quiet ones so they read on top when a stack can't fully separate.
            zIndexOffset: isNews ? 3000 : 2000,
        }).addTo(map);
        label.bindPopup(_burstPopupHtml(burst), { offset: [0, 0], minWidth: 210 });
    }

    return {
        circle, label, isLive, labelText,
        labelKey: _burstLabelKey(burst),
        priority: _burstLabelPriority(burst),
        sig: _burstPopupSig(burst),
    };
}


function _removeBurstLayers(entry) {
    map.removeLayer(entry.circle);
    if (entry.label) map.removeLayer(entry.label);
}


/**
 * Update burst circle overlays on the map.
 *
 * Draws a circle for every burst in the display set (live + recently-ended
 * stories, all nationwide — no proximity filter), keyed by burst id so
 * sequential bursts sharing a city scope don't collapse. Live bursts pulse;
 * ended ones render calm + carry a tappable centre label (their AI title),
 * since their alert pins have aged off the map. Removes circles for bursts no
 * longer in the set.
 */
// Repaint what bakes a token colour in when drawn — the burst circles and the
// beam/route lines — when the theme changes (config.js has re-read the tokens
// by now). Markers, banner and cards are CSS-coloured and follow on their own.
document.addEventListener('zl:themechange', () => {
    for (const layers of burstCircles.values()) {
        layers.circle.setStyle({ color: BURST_ACCENT_COLOR, fillColor: BURST_ACCENT_COLOR });
    }
    trackingBeam?.setStyle({ color: SERVICES[trackingBeamService]?.color || '#666' });
    routePolyline?.setStyle({ color: SERVICES[routePolylineService]?.color || '#3388ff' });
});

export function updateBurstCircles() {
    if (!map) return;

    const seen = new Set();

    for (const burst of activeBursts.values()) {
        if (!burst.centroid_lat || !burst.centroid_lon) continue;
        const id = burst.id || burst.scope;
        seen.add(id);

        const isLive = burst.is_active !== false;
        const labelKey = _burstLabelKey(burst);
        const existing = burstCircles.get(id);

        // Recreate when the live/story treatment or the centre-label key
        // changes — className and divIcon html can't be toggled on the live
        // layers. The key folds in the news treatment, so a story that links
        // news (quiet tag → hero pill) rebuilds; a plain title landing does too.
        if (existing) {
            if (existing.isLive === isLive && existing.labelKey === labelKey) {
                // Keep declutter priority fresh even without a rebuild (e.g. the
                // member count crept up) so stacking order tracks the latest data.
                existing.priority = _burstLabelPriority(burst);
                // Follow centroid drift (a live burst's centre shifts as it
                // clusters) — move the label with the circle, not just the circle.
                const center = [burst.centroid_lat, burst.centroid_lon];
                existing.circle.setLatLng(center);
                if (existing.label) existing.label.setLatLng(center);
                // Refresh the popup card if its content changed (e.g. the news
                // count landed), else it stays frozen.
                const sig = _burstPopupSig(burst);
                if (existing.sig !== sig) {
                    const html = _burstPopupHtml(burst);
                    existing.circle.setPopupContent(html);
                    if (existing.label) existing.label.setPopupContent(html);
                    existing.sig = sig;
                }
                continue;
            }
            _removeBurstLayers(existing);
            burstCircles.delete(id);
        }

        burstCircles.set(id, _createBurstLayers(burst));
    }

    // Remove layers for bursts no longer in the set
    for (const [id, entry] of burstCircles) {
        if (!seen.has(id)) {
            _removeBurstLayers(entry);
            burstCircles.delete(id);
        }
    }

    updateBurstMarkers();
    scheduleDeclutter();
}


// ── Burst label declutter ──────────────────────────────────────────
//
// Story pieken persist for 24h, so at low zoom ~30+ of them pile into the
// Randstad — far too many to all carry a full text pill (especially on a phone).
// So we CULL by priority: place the loudest (news-linked, then biggest) first,
// give each a small lift to dodge a near-miss, and if it still can't fit, hide
// its pill and let the burst's circle stand in for it (the ring is distinct from
// the round alert pins, so nothing reads as a lost alert). Zooming in spreads
// the centroids so more labels fit and reveal themselves. Runs after every burst
// update and on ``zoomend moveend`` (pixel positions change with the
// projection). Shown labels never overlap each other.

// px offset from the centroid dot to the pill's bottom edge at --dy:0. Keep in
// sync with the connector base height in styles.css.
const _LABEL_BASE_GAP = 10;
const _LABEL_STEP = 8;          // px lifted per collision iteration
const _LABEL_LIFT_BUDGET = 40;  // small: nudge past a near-miss, never build towers
const _LABEL_PAD = 4;           // breathing room added around each pill box

let _declutterRaf = null;
function scheduleDeclutter() {
    // Coalesce bursts of updates into one pass on the next frame — also lets the
    // just-created pills lay out so ``offsetWidth`` is real, not 0.
    if (_declutterRaf) return;
    _declutterRaf = requestAnimationFrame(() => {
        _declutterRaf = null;
        declutterBurstLabels();
    });
}

function declutterBurstLabels() {
    if (!map) return;

    // Pass 1 — all writes: un-hide every label (a label culled on a previous
    // pass is display:none and would measure as 0). Batching every un-hide
    // before any measurement keeps this to a single forced reflow instead of
    // one per label — the jank would otherwise land on the many-label / phone
    // case this whole feature targets.
    const refs = [];
    for (const entry of burstCircles.values()) {
        if (!entry.label) continue;
        const wrap = entry.label.getElement();
        if (!wrap) continue;
        wrap.style.display = '';
        const inner = wrap.querySelector('.piek-label');
        const pill = wrap.querySelector('.piek-label__pill');
        if (!inner || !pill) continue;
        refs.push({ entry, wrap, inner, pill });
    }
    // Pass 2 — all reads: measure after every un-hide is applied. --dy lives on
    // the inner .piek-label (its inline --dy:0px shadows any inherited value),
    // so the lift is written there, not on the wrapper getElement() returns.
    const items = refs.map(({ entry, wrap, inner, pill }) => {
        const pt = map.latLngToContainerPoint(entry.label.getLatLng());
        return {
            wrap,
            inner,
            x: pt.x,
            y: pt.y,
            w: (pill.offsetWidth || 120) + _LABEL_PAD * 2,
            h: (pill.offsetHeight || 24) + _LABEL_PAD * 2,
            priority: entry.priority || 0,
        };
    });
    // Loudest first: news/big pieken claim a label; quieter ones yield to their
    // circle when there's no room.
    items.sort((a, b) => b.priority - a.priority);

    const placed = [];
    const boxAt = (it, dy) => {
        const bottom = it.y - _LABEL_BASE_GAP - dy;
        return { left: it.x - it.w / 2, right: it.x + it.w / 2, top: bottom - it.h, bottom };
    };
    const hits = (a, b) =>
        !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

    for (const it of items) {
        let dy = 0;
        let box = boxAt(it, dy);
        // Small lift to dodge a near-collision — but never a tower.
        while (dy < _LABEL_LIFT_BUDGET && placed.some(p => hits(p, box))) {
            dy += _LABEL_STEP;
            box = boxAt(it, dy);
        }
        if (placed.some(p => hits(p, box))) {
            // Still colliding after the lift budget → cull to circle-only.
            it.wrap.style.display = 'none';
        } else {
            it.inner.style.setProperty('--dy', `${dy}px`);
            placed.push(box);
        }
    }
}


/**
 * Give the alert markers that fall inside a burst circle an accent ring, so
 * they read as part of that burst — distinct from standalone alerts. Membership
 * is spatial (within the drawn circle of any active burst), which matches what
 * the user sees. Runs on both burst and alert updates; a no-op until markers
 * and bursts exist. Also clears the ring when a burst ages off the map.
 */
export function updateBurstMarkers() {
    const centroids = [];
    for (const burst of activeBursts.values()) {
        if (burst.centroid_lat && burst.centroid_lon) {
            centroids.push([burst.centroid_lat, burst.centroid_lon]);
        }
    }

    markers.forEach((marker, id) => {
        const el = marker.getElement();
        if (!el) return;
        const icon = el.querySelector('.marker-icon');
        if (!icon) return;

        const alert = alerts.get(id);
        let inBurst = false;
        if (alert && alert.latitude && alert.longitude) {
            for (const [clat, clon] of centroids) {
                if (getDistanceKm(alert.latitude, alert.longitude, clat, clon) * 1000 <= BURST_CIRCLE_RADIUS_M) {
                    inBurst = true;
                    break;
                }
            }
        }
        icon.classList.toggle('in-burst', inBurst);
    });
}


// Pixel size of a grouping cell (~one marker diameter). Pins whose screen
// positions fall in the same cell are "overlapping" and share one count.
const OVERLAP_CELL_PX = 26;

/**
 * Badge overlapping pins with how many alerts stack there — at any zoom.
 *
 * Pins are clustered in screen space, so a burst's pins that sit on top of each
 * other when zoomed out get a count just like multi-unit dispatches at the same
 * address do up close. Exactly ONE badge is placed per visual cluster — on the
 * front-most (largest screen y, which Leaflet draws on top) member — so stacked
 * pins never render duplicate or disagreeing counts. Every pin stays on the map
 * (no collapsing). Recomputed on the alert-update cadence and on zoom/pan (bound
 * in initMap), since the projection changes the grouping.
 */
export function updateOverlapBadges() {
    if (!map) return;

    // Burst centroids, so a cluster whose alerts fall inside a burst gets the
    // burst-accent count (an incident) vs a neutral count (unrelated pile-up).
    const centroids = [];
    for (const burst of activeBursts.values()) {
        if (burst.centroid_lat && burst.centroid_lon) {
            centroids.push([burst.centroid_lat, burst.centroid_lon]);
        }
    }
    const isInBurst = (a) => centroids.some(([clat, clon]) =>
        getDistanceKm(a.latitude, a.longitude, clat, clon) * 1000 <= BURST_CIRCLE_RADIUS_M);

    // Project every shown, located pin to screen space. Count only shown pins,
    // so the badge matches what's on the map — addMarkerToMap gates visibility
    // on the same service filter.
    const pts = [];
    alerts.forEach((alert, id) => {
        if (!alert.latitude || !alert.longitude) return;
        if (!activeFilters.has(alert.service)) return;
        if (!markers.has(id)) return;
        const p = map.latLngToLayerPoint([alert.latitude, alert.longitude]);
        pts.push({ id, x: p.x, y: p.y, inBurst: isInBurst(alert) });
    });

    // Greedy screen-space clustering: pins within OVERLAP_CELL_PX of a seed join
    // its cluster. One badge per cluster (N≤100, so O(N²) is trivial), on the
    // front-most member — no grid, so no boundary split into two disagreeing
    // counts. badgeFor holds the single marker id per cluster that gets a badge.
    const R2 = OVERLAP_CELL_PX * OVERLAP_CELL_PX;
    const assigned = new Set();
    const badgeFor = new Map(); // marker id -> { count, burst }
    for (const seed of pts) {
        if (assigned.has(seed.id)) continue;
        assigned.add(seed.id);
        const group = [seed];
        for (const other of pts) {
            if (assigned.has(other.id)) continue;
            const dx = other.x - seed.x;
            const dy = other.y - seed.y;
            if (dx * dx + dy * dy <= R2) {
                assigned.add(other.id);
                group.push(other);
            }
        }
        if (group.length > 1) {
            const rep = group.reduce((a, b) => (b.y > a.y ? b : a));
            badgeFor.set(rep.id, { count: group.length, burst: group.some(g => g.inBurst) });
        }
    }

    markers.forEach((marker, id) => {
        const el = marker.getElement();
        if (!el) return;
        const icon = el.querySelector('.marker-icon');
        if (!icon) return;

        const info = badgeFor.get(id);
        let badge = icon.querySelector('.marker-count');
        if (info) {
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'marker-count';
                icon.appendChild(badge);
            }
            badge.textContent = String(info.count);
            badge.classList.toggle('burst', info.burst);
        } else if (badge) {
            badge.remove();
        }
    });
}
